import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { toPersian } from '../../utils/persianNumber';
import { useWorkspaceNav } from '../../store/WorkspaceContext';
import { isWorkspaceEligiblePath, workspaceTabTitle } from '../../utils/workspaceConfig';
import { getDashboardShortcuts } from '../../utils/dashboardShortcuts';
import {
  NotePencil, Ticket, HourglassMedium, User, Plus, Users,
  Car, ClipboardText, Star, Export, Robot, Tray, Buildings,
} from '@phosphor-icons/react';

const SHORTCUT_ICONS = {
  'new-ticket': NotePencil,
  tickets: Ticket,
  pending: HourglassMedium,
  profile: User,
  add: Plus,
  users: Users,
  missions: Car,
  report: ClipboardText,
  feedback: Star,
  referred: Export,
  ai: Robot,
  queue: Tray,
  companies: Buildings,
};

const COUNTS_POLL_MS = 10000;

// Dispatcher shortcut id -> which counter it shows. 'ai-routing' works the
// same unrouted queue as 'queue' (see DispatcherPage), so they share a count.
const DISPATCHER_COUNT_KEY = { queue: 'queue', 'ai-routing': 'queue', assigned: 'assigned' };

const listLength = (res) => (Array.isArray(res.data) ? res.data : res.data?.tickets || []).length;

function useDispatcherCounts(enabled) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = async () => {
      const [queue, assigned] = await Promise.all([
        ticketApi.getTicketListView({ dispatcher_seen: 0 }).then(listLength).catch(() => undefined),
        ticketApi.getAssignedUnansweredTickets().then(listLength).catch(() => undefined),
      ]);
      if (!alive) return;
      setCounts((prev) => ({
        queue: queue ?? prev.queue,
        assigned: assigned ?? prev.assigned,
      }));
    };
    load();
    const timer = setInterval(load, COUNTS_POLL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [enabled]);
  return counts;
}

/**
 * Compact role-aware shortcut row for dashboard pages.
 * `onAction` handles in-page actions (filter/section switches).
 */
export default function DashboardShortcuts({ role, onAction, activePath, activeSection, activeFilter }) {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceNav = useWorkspaceNav();
  const shortcuts = getDashboardShortcuts(role);
  const counts = useDispatcherCounts(role === 'dispatcher');
  const currentPath = activePath || location.pathname;

  // Dispatcher dashboard has no shortcut menu (removed by request).
  if (role === 'dispatcher' || !shortcuts.length) return null;

  const handleClick = (item) => {
    if (item.action && onAction) {
      onAction(item.action);
      if (item.action.startsWith('filter:') || item.action.startsWith('section:')) return;
    }
    if (workspaceNav && isWorkspaceEligiblePath(item.path)) {
      workspaceNav(item.path, workspaceTabTitle(item.path));
      navigate('/dashboard');
      return;
    }
    navigate(item.path);
  };

  const isActive = (item) => {
    if (item.action?.startsWith('section:')) {
      const sectionId = item.action.slice('section:'.length);
      return activeSection === sectionId;
    }
    if (item.action?.startsWith('filter:')) {
      const filterId = item.action.slice('filter:'.length);
      return activeFilter === filterId;
    }
    return item.path === currentPath;
  };

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
      {shortcuts.map((item) => {
        const active = isActive(item);
        const primary = item.primary;
        const Icon = SHORTCUT_ICONS[item.icon] || Ticket;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleClick(item)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: primary ? '8px 16px' : '7px 14px',
              borderRadius: 10,
              border: primary ? 'none' : `1.5px solid ${active ? '#29AAE1' : '#e2e8f0'}`,
              background: primary
                ? 'linear-gradient(135deg,#29AAE1,#233A7A)'
                : active ? '#EDF3FC' : '#fff',
              color: primary ? '#fff' : active ? '#233A7A' : '#475569',
              fontWeight: primary || active ? 700 : 500,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              boxShadow: primary ? '0 3px 10px rgba(35,58,122,0.25)' : 'none',
            }}
          >
            <Icon size={15} weight="regular" />
            <span>{item.label}</span>
            {role === 'dispatcher' && counts[DISPATCHER_COUNT_KEY[item.id]] !== undefined && (
              <span style={{
                minWidth: 20, padding: '1px 6px', borderRadius: 10,
                background: active ? '#29AAE1' : '#f1f5f9',
                color: active ? '#fff' : '#64748b',
                fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
              }}>
                {toPersian(counts[DISPATCHER_COUNT_KEY[item.id]])}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
